import React, { useState, useEffect } from 'react';
import { X, Tag as TagIcon } from 'lucide-react';

/**
 * Reusable modal for editing the tags array on a saved exercise — works for
 * both `custom_exercises` and `saved_library_refs` (same shape, same
 * normalization rules).
 *
 * The parent passes the current tags array and an async onSave handler that
 * does the API call. We don't do the normalization here — the backend does it
 * authoritatively, then echoes back the canonical list, which the parent uses
 * to update local state.
 */
const TagEditModal = ({ open, exerciseName, currentTags = [], onSave, onClose }) => {
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setDraft((currentTags || []).join(', '));
      setError('');
    }
  }, [open, currentTags]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    const tagList = draft.split(',').map((t) => t.trim().toLowerCase()).filter(Boolean);
    try {
      await onSave(Array.from(new Set(tagList)));
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Could not update tags.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="tag-edit-modal">
      <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <TagIcon className="w-4 h-4 text-enf-accent-fg" />
            <h3 className="font-display text-xl tracking-wide leading-none">Edit tags</h3>
          </div>
          <button onClick={onClose} className="text-enf-text-muted hover:text-enf-text-primary" data-testid="tag-edit-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="text-xs text-enf-text-muted mb-3 truncate">{exerciseName}</div>
        <form onSubmit={submit}>
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="e.g. forearm-day, push-day, combatives"
            className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-sm text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
            data-testid="tag-edit-input"
          />
          <div className="text-[10px] text-enf-text-muted mt-1">Comma-separated. Tags are auto-lowercased and deduplicated.</div>
          {error && <div className="text-xs text-enf-accent-fg mt-2">{error}</div>}
          <div className="flex justify-end gap-2 mt-4">
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-xs font-semibold text-enf-text-secondary hover:text-enf-text-primary" data-testid="tag-edit-cancel">
              Cancel
            </button>
            <button type="submit" disabled={busy} className="px-3 py-1.5 bg-enf-accent hover:bg-enf-accent-hover disabled:bg-enf-surface-elevated disabled:text-enf-text-muted rounded-md text-xs font-semibold transition-colors" data-testid="tag-edit-save">
              {busy ? 'Saving…' : 'Save tags'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TagEditModal;
